import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { lookupLabel } from '../data/lookups';
import { lineupStatusOptions, matchStatusOptions } from '../data/options';
import type { LookupState, MatchEventRecord, TimeFormat, UserPreferences } from '../types';
import {
  buildCaptainWelcomeMessage,
  buildLineupMessage,
  formatCommunicationDate,
  formatMatchSelectDate,
  opposingCaptainNames,
  type CommunicationTeam,
} from '../utils/communications';
import {
  buildScoreConfirmation,
  buildTeamResults,
  type MatchLineScoreDraft,
  type ScoreLineStatus,
  type ScoreWinner,
} from '../utils/matchScoreEntry';
import { formatTime } from '../utils/timeFormat';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { GroupMeTeamUpdates } from './GroupMeTeamUpdates';
import {
  communicationMatchDateBounds,
  communicationMatchTimeframeOptions,
  type CommunicationMatchTimeframe,
} from '../utils/communicationMatchFilters';
import { WelcomeEmailSettings } from './WelcomeEmailSettings';

type CommunicationsProps = {
  initialMatchId?: string | null;
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  timeFormat: TimeFormat;
  userPreferences: UserPreferences;
  writableTeamIds?: string[];
  onConfigureGroupMe?: (teamId: string) => void;
};

type Snack = {
  severity: 'success' | 'error' | 'warning' | 'info';
  message: string;
};
type CommunicationsViewState = {
  selectedMatchId: string;
  selectedTeamId: string;
  timeframe: CommunicationMatchTimeframe;
  activeTab?: 'match' | 'groupme' | 'welcome';
};

function optionLabel(options: { value: string; label: string }[], value: unknown): string {
  if (typeof value !== 'string') return '';
  return options.find((option) => option.value === value)?.label || value;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateIso: string, days: number): string {
  const date = new Date(`${dateIso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function scoreStatus(resultValue: string, retired: boolean, defaulted: boolean, walkover: boolean): ScoreLineStatus {
  if (resultValue === 'Double Default') return 'Double Default';
  if (retired || ['Retired Win', 'Retired Loss'].includes(resultValue)) return 'Retired';
  if (defaulted || walkover || ['Default by Opponent', 'Default by Us'].includes(resultValue)) return 'Default';
  return 'Completed';
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
    </Box>
  );
}

const CAPTAIN_TEAMS_SCOPE = '__captain_teams__';

export function Communications({ initialMatchId, lookups, defaultCaptainTeamIds, timeFormat, userPreferences, writableTeamIds = [], onConfigureGroupMe }: CommunicationsProps) {
  const auditUserId = useAuditUserId();
  const { isOwner, appUser } = useAuth();
  const canManageWelcomeTemplate = isOwner || appUser?.role === 'admin';
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<CommunicationsViewState>({
    userId: auditUserId,
    routeKey: 'communications',
    defaultState: { selectedMatchId: '', selectedTeamId: CAPTAIN_TEAMS_SCOPE, timeframe: 'current_window', activeTab: 'match' },
  });
  const [matches, setMatches] = useState<MatchEventRecord[]>([]);
  const [teams, setTeams] = useState<CommunicationTeam[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState(initialMatchId || savedViewState.selectedMatchId);
  const [selectedTeamId, setSelectedTeamId] = useState(initialMatchId ? CAPTAIN_TEAMS_SCOPE : savedViewState.selectedTeamId || '');
  const [timeframe, setTimeframe] = useState<CommunicationMatchTimeframe>(savedViewState.timeframe || 'current_window');
  const [activeTab, setActiveTab] = useState<'match' | 'groupme' | 'welcome'>(savedViewState.activeTab || 'match');
  const [loading, setLoading] = useState(false);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreLines, setScoreLines] = useState<MatchLineScoreDraft[]>([]);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [manualCopyMessage, setManualCopyMessage] = useState('');
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'communications', ready: !loading && !scoreLoading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedMatchId(initialMatchId || savedViewState.selectedMatchId);
    setSelectedTeamId(initialMatchId ? CAPTAIN_TEAMS_SCOPE : savedViewState.selectedTeamId || '');
    setTimeframe(savedViewState.timeframe || 'current_window');
    setActiveTab(savedViewState.activeTab || 'match');
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, initialMatchId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedMatchId, selectedTeamId, timeframe, activeTab });
  }, [activeTab, selectedMatchId, selectedTeamId, setSavedViewState, timeframe, viewStateKey, viewStateReady]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const bounds = communicationMatchDateBounds(timeframe, today());
    let matchQuery = supabase
      .from('match_events')
      .select('*')
      .neq('match_status', 'Cancelled')
      .is('deleted_at', null);
    if (selectedTeamId === CAPTAIN_TEAMS_SCOPE && defaultCaptainTeamIds.length > 0) matchQuery = matchQuery.in('team_id', defaultCaptainTeamIds);
    else if (selectedTeamId && selectedTeamId !== CAPTAIN_TEAMS_SCOPE) matchQuery = matchQuery.eq('team_id', selectedTeamId);
    if (bounds.from) matchQuery = matchQuery.gte('match_date', bounds.from);
    if (bounds.to) matchQuery = matchQuery.lte('match_date', bounds.to);
    const [matchResult, teamResult] = await Promise.all([
      selectedTeamId === CAPTAIN_TEAMS_SCOPE && defaultCaptainTeamIds.length === 0
        ? Promise.resolve({ data: [] as MatchEventRecord[], error: null })
        : matchQuery.order('match_date', { ascending: true }).order('match_time', { ascending: true }).limit(200),
      supabase
        .from('teams')
        .select('id, team_name, league_name, home_facility_id, home_match_opponent_message_template')
        .is('deleted_at', null),
    ]);
    setLoading(false);

    const error = matchResult.error || teamResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const availableMatches = (matchResult.data || []) as unknown as MatchEventRecord[];
    setMatches(availableMatches);
    setTeams((teamResult.data || []) as unknown as CommunicationTeam[]);
    setSelectedMatchId((current) => {
      if (initialMatchId && availableMatches.some((match) => match.id === initialMatchId)) return initialMatchId;
      if (current && availableMatches.some((match) => match.id === current)) return current;
      return current ? '' : availableMatches.find((match) => match.match_date >= today())?.id || availableMatches.at(-1)?.id || '';
    });
  }, [defaultCaptainTeamIds, initialMatchId, selectedTeamId, timeframe]);

  useEffect(() => {
    if (lookups.teams.length === 0) return;
    setSelectedTeamId((current) => current === CAPTAIN_TEAMS_SCOPE || current === '' || lookups.teams.some((team) => team.value === current) ? current : CAPTAIN_TEAMS_SCOPE);
  }, [lookups.teams]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const selectedMatch = matches.find((match) => match.id === selectedMatchId) || null;
  const selectedTeam = selectedMatch ? teamById.get(selectedMatch.team_id) || null : null;
  const selectedFacilityId = selectedMatch?.facility_id || (selectedMatch?.match_site_type === 'Home' ? selectedTeam?.home_facility_id || null : null);

  useEffect(() => {
    const loadScores = async () => {
      if (!selectedMatchId) {
        setScoreLines([]);
        return;
      }
      setScoreLines([]);
      setScoreLoading(true);
      const { data, error } = await supabase
        .from('line_results')
        .select('league_configuration_position_id, position_code, line_number, line_type, winner, completion_status, set1_us_games, set1_opponent_games, set2_us_games, set2_opponent_games, set3_us_games, set3_opponent_games, retired, defaulted, walkover, notes, result_value, league_configuration_positions(display_name, display_order, line_type)')
        .eq('match_event_id', selectedMatchId)
        .is('deleted_at', null)
        .order('line_number', { ascending: true });
      setScoreLoading(false);
      if (error) {
        setScoreLines([]);
        setSnack({ severity: 'error', message: error.message });
        return;
      }
      const lines = ((data || []) as unknown as Array<{
        league_configuration_position_id: string;
        position_code: string;
        line_number: number | null;
        line_type: string | null;
        winner: ScoreWinner;
        completion_status: 'Not Started' | 'In Progress' | 'Complete';
        set1_us_games: number | null;
        set1_opponent_games: number | null;
        set2_us_games: number | null;
        set2_opponent_games: number | null;
        set3_us_games: number | null;
        set3_opponent_games: number | null;
        retired: boolean;
        defaulted: boolean;
        walkover: boolean;
        notes: string | null;
        result_value: string;
        league_configuration_positions?: { display_name?: string; display_order?: number; line_type?: string } | Array<{ display_name?: string; display_order?: number; line_type?: string }>;
      }>).map((row) => {
        const position = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
        return {
          positionId: row.league_configuration_position_id,
          positionCode: row.position_code,
          displayName: position?.display_name || row.position_code,
          displayOrder: row.line_number || position?.display_order || 0,
          lineType: row.line_type || position?.line_type || 'other',
          winner: row.winner,
          status: scoreStatus(row.result_value, row.retired, row.defaulted, row.walkover),
          completionStatus: row.completion_status,
          sets: [
            { us: row.set1_us_games, opponent: row.set1_opponent_games },
            { us: row.set2_us_games, opponent: row.set2_opponent_games },
            { us: row.set3_us_games, opponent: row.set3_opponent_games },
          ],
          retired: row.retired,
          defaulted: row.defaulted,
          walkover: row.walkover,
          notes: row.notes || '',
        };
      });
      setScoreLines(lines);
    };
    void loadScores();
  }, [selectedMatchId]);

  const selectedMatchLabel = (match: MatchEventRecord): string => {
    const teamName = lookupLabel(lookups, 'teams', match.team_id) || teamById.get(match.team_id)?.team_name || 'Team';
    return `${formatMatchSelectDate(match.match_date)} - ${teamName} vs ${match.opponent_name || 'TBD'}`;
  };

  const copyText = async (message: string, successMessage = 'Copied to clipboard.') => {
    try {
      await navigator.clipboard.writeText(message);
      setManualCopyMessage('');
      setSnack({ severity: 'success', message: successMessage });
    } catch {
      setManualCopyMessage(message);
      setSnack({ severity: 'info', message: 'Clipboard blocked. Copy the message from the dialog.' });
    }
  };

  const copyCaptainWelcomeMessage = async () => {
    if (!selectedMatch) return;
    try {
      const { message, warnings } = await buildCaptainWelcomeMessage(selectedMatch, selectedTeam, timeFormat);
      await copyText(message, warnings.length > 0 ? `Copied to clipboard. Warning: ${warnings.join(', ')}.` : 'Copied to clipboard.');
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to copy message.' });
    }
  };

  const copyLineup = async (external: boolean) => {
    if (!selectedMatch) return;
    try {
      const teamName = lookupLabel(lookups, 'teams', selectedMatch.team_id) || selectedTeam?.team_name || 'Team';
      const message = await buildLineupMessage(selectedMatch, teamName, userPreferences, timeFormat, external, selectedFacilityId);
      await copyText(message);
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to copy lineup.' });
    }
  };

  const copyScoreMessage = async (kind: 'confirmation' | 'team') => {
    if (!selectedMatch || scoreLines.length === 0) return;
    const teamName = lookupLabel(lookups, 'teams', selectedMatch.team_id) || selectedTeam?.team_name || 'Team';
    const message = kind === 'confirmation'
      ? buildScoreConfirmation(scoreLines)
      : buildTeamResults(scoreLines, teamName, selectedMatch.opponent_name || 'Opponent');
    await copyText(message, kind === 'confirmation' ? 'Score confirmation copied.' : 'Team results copied.');
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Communications</Typography>
        <Typography variant="body2" color="text.secondary">
          Create and manage team communications.
        </Typography>
      </Box>

      <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} aria-label="Communications sections">
        <Tab value="match" label="Match Communications" />
        <Tab value="groupme" label="GroupMe Updates" />
        {canManageWelcomeTemplate && <Tab value="welcome" label="Player Welcome Email" />}
      </Tabs>

      {activeTab === 'groupme' && <GroupMeTeamUpdates teams={teams} defaultCaptainTeamIds={defaultCaptainTeamIds} writableTeamIds={writableTeamIds} onConfigureGroupMe={onConfigureGroupMe} />}
      {activeTab === 'welcome' && <WelcomeEmailSettings />}

      {activeTab === 'match' && <>

      {loading && <LinearProgress />}
      {selectedTeamId === CAPTAIN_TEAMS_SCOPE && defaultCaptainTeamIds.length === 0 && <Alert severity="info">You aren't assigned as captain or co-captain of a team. Select another authorized team or All Teams.</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Match Selection</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(200px, 0.75fr)' }, gap: 1.5, maxWidth: { xs: 'none', md: 840 } }}>
            <FormControl fullWidth>
              <InputLabel>Team</InputLabel>
              <Select label="Team" value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
                <MenuItem value={CAPTAIN_TEAMS_SCOPE}>My Captain Teams</MenuItem>
                <MenuItem value="">All Teams</MenuItem>
                {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Timeframe</InputLabel>
              <Select label="Timeframe" value={timeframe} onChange={(event) => setTimeframe(event.target.value as CommunicationMatchTimeframe)}>
                {communicationMatchTimeframeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth disabled={matches.length === 0} sx={{ maxWidth: { xs: 'none', md: 840 } }}>
            <InputLabel>Matches</InputLabel>
            <Select label="Matches" value={selectedMatchId} onChange={(event) => setSelectedMatchId(event.target.value)}>
              {matches.map((match) => (
                <MenuItem key={match.id} value={match.id}>{selectedMatchLabel(match)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {matches.length === 0 && !loading && (
            <Alert severity="info">No matches in this timeframe.</Alert>
          )}
        </Stack>
      </Paper>

      {selectedMatch && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Match Summary</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Detail label="Team" value={lookupLabel(lookups, 'teams', selectedMatch.team_id) || selectedTeam?.team_name || ''} />
              <Detail label="Opponent" value={selectedMatch.opponent_name || ''} />
              <Detail label="Match Date" value={formatCommunicationDate(selectedMatch.match_date)} />
              <Detail label="Match Time" value={formatTime(selectedMatch.match_time, timeFormat)} />
              <Detail label="Warmup Time" value={formatTime(selectedMatch.warmup_time, timeFormat)} />
              <Detail label="Facility" value={lookupLabel(lookups, 'facilities', selectedFacilityId)} />
              <Detail label="Match Status" value={optionLabel(matchStatusOptions, selectedMatch.match_status || 'Draft')} />
              <Detail label="Lineup Status" value={optionLabel(lineupStatusOptions, selectedMatch.lineup_status || 'Not Started')} />
            </Box>
          </Stack>
        </Paper>
      )}

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>Communication Actions</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>Captain&apos;s Welcome Message</Typography><Typography variant="body2" color="text.secondary">A ready-to-send welcome for the opposing captain.</Typography></Box>
            </Stack>
            {selectedMatch && <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Opposing Captains</Typography>
              {opposingCaptainNames(selectedMatch.opponent_captain_names).length > 0
                ? opposingCaptainNames(selectedMatch.opponent_captain_names).map((name, index) => <Typography variant="body2" key={`${name}-${index}`}>{name}</Typography>)
                : <Typography variant="body2" color="text.secondary">Not provided</Typography>}
            </Box>}
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button fullWidth variant="contained" startIcon={<ContentCopyIcon />} disabled={!selectedMatch} onClick={() => void copyCaptainWelcomeMessage()}>
              Copy Captain&apos;s Welcome Message
            </Button>
          </CardActions>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>Lineup - Internal</Typography><Typography variant="body2" color="text.secondary">Team-facing lineup message.</Typography></Box>
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button fullWidth variant="contained" startIcon={<ContentCopyIcon />} disabled={!selectedMatch} onClick={() => void copyLineup(false)}>
              Copy Lineup - Internal
            </Button>
          </CardActions>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>Lineup - External</Typography><Typography variant="body2" color="text.secondary">Opponent-facing lineup message.</Typography></Box>
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button fullWidth variant="contained" startIcon={<ContentCopyIcon />} disabled={!selectedMatch} onClick={() => void copyLineup(true)}>
              Copy Lineup - External
            </Button>
          </CardActions>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Score Confirmation</Typography>
                <Typography variant="body2" color="text.secondary">Message for the opposing captain.</Typography>
              </Box>
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button fullWidth variant="contained" startIcon={<ContentCopyIcon />} disabled={!selectedMatch || scoreLoading || scoreLines.length === 0} onClick={() => void copyScoreMessage('confirmation')}>
              Copy Score Confirmation
            </Button>
          </CardActions>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Results</Typography>
                <Typography variant="body2" color="text.secondary">Internal team or GroupMe summary.</Typography>
              </Box>
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button fullWidth variant="contained" startIcon={<ContentCopyIcon />} disabled={!selectedMatch || scoreLoading || scoreLines.length === 0} onClick={() => void copyScoreMessage('team')}>
              Copy Team Results
            </Button>
          </CardActions>
        </Card>
      </Box>
      </Box>

      </>}

      <Dialog open={Boolean(manualCopyMessage)} onClose={() => setManualCopyMessage('')} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Copy Message</Typography>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
            {manualCopyMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setManualCopyMessage('')}>Close</Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
